import { useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { AccountAvatar } from "@/features/accounts/AccountAvatar";
import {
  accountHasCustomDisplayLabel,
  getAccountDisplayLabel,
} from "@/lib/mail/accountDisplayLabel";
import { cn } from "@/lib/cn";
import type { MailAccountSummary } from "@/lib/types/mail";

type Props = {
  accounts: MailAccountSummary[];
  accountId: number;
  disabled?: boolean;
  onChange: (accountId: number) => void;
};

function AccountOptionContent({
  account,
  compact = false,
}: {
  account: MailAccountSummary;
  compact?: boolean;
}) {
  const label = getAccountDisplayLabel(account);
  const hasCustom = accountHasCustomDisplayLabel(account);

  return (
    <>
      <AccountAvatar account={account} size="sm" />
      <span className="min-w-0 flex-1 text-left">
        <span
          className={cn(
            "block truncate text-[var(--mail-text)]",
            compact ? "text-sm font-medium" : "text-sm"
          )}
        >
          {label}
        </span>
        {hasCustom ? (
          <span className="block truncate text-[11px] text-[var(--mail-muted)]">{account.email}</span>
        ) : null}
      </span>
      {account.isDefault ? (
        <span className="compose-account-default-badge shrink-0">Par défaut</span>
      ) : null}
    </>
  );
}

export function ComposeAccountSelect({ accounts, accountId, disabled, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = accounts.find((a) => a.id === accountId) ?? accounts[0];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (accounts.length < 2 || !selected) return null;

  return (
    <div ref={rootRef} className="compose-field-row compose-account-select relative">
      <span className="compose-field-label" id={`${listId}-label`}>
        De
      </span>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-labelledby={`${listId}-label`}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "compose-account-trigger flex min-w-0 flex-1 items-center gap-2 rounded-lg py-0.5 text-left transition",
          disabled ? "cursor-not-allowed opacity-60" : "hover:bg-[var(--mail-surface-hover)]"
        )}
      >
        <AccountOptionContent account={selected} compact />
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-[var(--mail-muted)] transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-labelledby={`${listId}-label`}
          className="compose-account-menu mail-panel absolute left-[3.5rem] right-3 top-full z-20 mt-1 max-h-56 overflow-y-auto py-1 shadow-[var(--mail-shadow-md)]"
        >
          {accounts.map((acc) => {
            const isSelected = acc.id === accountId;
            return (
              <li key={acc.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(acc.id);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2.5 text-left transition",
                    isSelected
                      ? "bg-[var(--mail-accent-soft)] text-[var(--mail-accent-dark)]"
                      : "text-[var(--mail-text)] hover:bg-[var(--mail-surface-hover)]"
                  )}
                >
                  <AccountOptionContent account={acc} />
                  {isSelected ? (
                    <Check className="h-4 w-4 shrink-0 text-[var(--mail-accent)]" aria-hidden />
                  ) : (
                    <span className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
